<template>
  <div
    class="form-check w-100 d-flex align-items-center justify-content-between"
    tabindex="0"
    :id="`collapse-${componentId}-${interestArea.value}`"
    @keydown.enter="handleExpand"
  >
    <div class="check">
      <input
        class="form-check-input"
        type="checkbox"
        :checked="allSelected"
        @click="handleSelectAll(interestArea)"
        :id="`${componentId}-${interestArea.value}-interest-input`"
        :aria-checked="allSelected"
        :aria-labelledby="`${componentId}-${interestArea.value}`"
      />
      <label
        class="form-check-label fs-small fw-bold"
        :for="`${componentId}-${interestArea.value}-interest-input`"
        :id="`${componentId}-${interestArea.value}-label`"
        >{{ interestArea.text }}
      </label>
    </div>
    <!-- toggle button -->
    <button
      class="btn btn-link fw-bold p-0 m-0"
      @click="handleExpand"
      :aria-label="`Toggle ${interestArea.text} section`"
      :aria-expanded="expanded"
      :aria-controls="`${componentId}-${interestArea.value}`"
    >
      <font-awesome-icon
        class="text-dark-3 icon-size"
        :icon="expanded ? 'chevron-up' : 'chevron-down'"
      ></font-awesome-icon>
    </button>
  </div>
  <Transition
    @before-enter="handleCollapseShown"
    @enter="handleCollapseShown"
    name="accordion"
    @before-leave="handleCollapseHidden"
    @leave="handleCollapseHidden"
  >
    <div
      v-show="expanded"
      class="collapse ms-space-sm"
      :id="`${componentId}-${interestArea.value}`"
      :class="{ show: interestArea.checked }"
      :aria-hidden="!expanded"
    >
      <div
        class="form-check mb-space-xxs"
        v-for="subject in interestArea.subjects"
        :key="subject.value"
      >
        <input
          class="form-check-input"
          type="checkbox"
          @click="handleCheckedSubject(subject)"
          :checked="selected?.[interestArea.value].has(subject.value)"
          :id="`${componentId}-${subject.value}-input`"
          :aria-checked="selected?.[interestArea.value].has(subject.value)"
          :aria-labelledby="`${componentId}-${subject.value}-label`"
        />
        <label
          class="form-check-label d-inline-flex align-items-center fs-small"
          :for="`${componentId}-${subject.value}-input`"
          :id="`${componentId}-${subject.value}-label`"
          >{{ subject.text }}
        </label>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import {
  FilterNestedModel,
  NestedCheckFilterEmit,
  NestedFilterCollapseEmit,
} from "~/components/degree-search/FilterCheckboxNested.vue";

import {
  FilterInterestAreaSubjects,
  FilterSubject,
} from "~/src/services/DegreeSearchService";
type Props = {
  options: FilterInterestAreaSubjects;
  componentId: string;
};

const props = defineProps<Props>();
const interestArea = ref(props.options);
const expanded = ref(false);
const selectAll = ref(false);
const trackSelected = ref(new Set<string>([]));

const selected = defineModel<FilterNestedModel>({});
/*
 ********************************************
 *  emits
 ********************************************
 */
const emits = defineEmits<{
  (e: "checked", value: NestedCheckFilterEmit): void;
  (e: "unchecked", value: NestedCheckFilterEmit): void;
  (e: "change", value: NestedCheckFilterEmit): void;
  (e: "collapse:hidden", value: NestedFilterCollapseEmit): void;
  (e: "collapse:shown", value: NestedFilterCollapseEmit): void;
  (
    e: "filterChange",
    evnt: NestedCheckFilterEmit,
    interestArea: FilterSubject[]
  ): void;
}>();

/*
 ********************************************
 *  methods
 ********************************************
 */
const handleExpand = () => {
  expanded.value = !expanded.value;
};
const handleSelectAll = (interestArea: FilterInterestAreaSubjects) => {
  selectAll.value = !selectAll.value;
  if (!allSelected.value) {
    interestArea.subjects.forEach((subject) => {
      selected.value?.[interestArea.value].add(subject.value);
      trackSelected.value.add(subject.value);
    });
  } else {
    interestArea.subjects.forEach((subject) => {
      selected.value?.[interestArea.value].delete(subject.value);
      trackSelected.value.delete(subject.value);
    });
  }
  emits(
    "filterChange",
    {
      section: "areas of interest",
      text: interestArea.text.toLowerCase(),
      checked: selectAll.value,
    },
    interestArea.subjects
  );
};

const initialLoad = ref(false);
const handleCollapseShown = (el: Element) => {
  if (el instanceof HTMLElement) {
    if (expanded.value) {
      el.style.height = "0px";
      el.style.height = el.scrollHeight + "px";
    } else if (initialLoad.value) {
      el.style.height = el.scrollHeight + "px";
      setTimeout(() => {
        el.style.height = "0px";
      }, 10);
    }
    initialLoad.value = true;
  }
};

const handleCollapseHidden = (el: HTMLElement | Element) => {
  if (el instanceof HTMLElement) {
    el.style.height = "0";
    expanded.value
      ? emits("collapse:shown", {
          event: "open",
          value: interestArea.value.value.toLowerCase(),
        })
      : emits("collapse:hidden", {
          event: "close",
          value: interestArea.value.value.toLowerCase(),
        });
  }
};

const handleCheckedSubject = async (subject: FilterSubject) => {
  if (selected?.value?.[interestArea.value.value].has(subject.value)) {
    selected.value?.[interestArea.value.value].delete(subject.value);
    trackSelected.value.delete(subject.value);
    emits("unchecked", {
      section: interestArea.value.value,
      text: subject.text,
      checked: false,
    });
  } else {
    selected.value?.[interestArea.value.value].add(subject.value);
    trackSelected.value.add(subject.value);
    emits("checked", {
      section: interestArea.value.value,
      text: subject.text,
      checked: true,
    });
  }
  subject.checked =
    selected?.value?.[interestArea.value.value].has(subject.value) || false;
  emits(
    "filterChange",
    {
      section: `areas of interest ^  ${subject.value.toLocaleLowerCase()}`,
      text: subject.text.toLocaleLowerCase(),
      checked:
        selected?.value?.[interestArea.value.value].has(subject.value) || false,
    },
    interestArea.value.subjects
  );
};

const allSelected = computed(() => {
  return (
    selected.value?.[interestArea.value.value].size ===
    interestArea.value.subjects.length
  );
});
</script>

<style scoped>
.icon-size {
  font-size: 1rem;
}
.accordion-enter-active,
.accordion-leave-active {
  will-change: height, opacity;
  transition: height 0.3s ease;
  overflow: hidden;
}
</style>
